import { useEffect, useRef } from "react";
import { motion, useInView, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import bg from "../../assets/images/about_bg.png";
function Counter({ value, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const motionVal = useMotionValue(0);
  const springVal = useSpring(motionVal, { damping: 30, stiffness: 100 });

  useEffect(() => {
    if (inView) motionVal.set(value);
  }, [inView, value, motionVal]);

  useEffect(() => {
    return springVal.on("change", (latest) => {
      if (ref.current) {
        ref.current.textContent = Math.floor(latest) + suffix;
      }
    });
  }, [springVal, suffix]);

  return (
    <span className="font-serif font-semibold tracking-wider" ref={ref}>
      0{suffix}
    </span>
  );
}

const PILLS = [
  "BPM Architecture",
  "Cloud Native",
  "Workflow AI",
  "Low-Code / No-Code",
  "Decision Automation",
  "Legacy Modernization",
];

export default function Welcome() {
  return (
    <section
      id="about-us"
      className="relative w-full lg:py-24 md:py-16 py-10 bg-[#ffffff] text-slate-900 overflow-hidden"
    >
      <div className="mx-auto 3xl:w-4/5 lg:w-11/12 px-6 md:px-12 relative z-10">
        <div className="flex flex-col items-start max-w-4xl md:mb-14 mb-6 lg:mb-20">
          <div className="inline-flex items-center gap-2 px-0.5 py-1.5 rounded-fullbackdrop-blur-md mb-6 shadow-xs">
            <span className="text-xs font-semibold tracking-widest text-slate-700 uppercase">
              Welcome to Truviq
            </span>
          </div>

          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#000036] leading-[1.12]">
            Where ideas are{" "}
            <span className="italic font-serif font-normal text-[#5dc9f0]">
              Re-imAIgined
            </span>{" "}
            through relentless innovation.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="md:col-span-7 md:p-4 p-0 flex flex-col justify-between border-b border-slate-200/80"
          >
            <div>
              <span className="text-xs font-semibold tracking-widest text-slate-400 uppercase">
                Our Purpose
              </span>
              <p className="mt-4 text-base md:text-xl font-normal text-slate-800 leading-snug">
                Truviq Systems is a renowned global IT consulting company
                committed to collaborating with our clients to create value,
                solve problems efficiently and build relationships that harbor
                trust. With 10+ years of experience in the Digital Process
                Transformation space, Truviq delivers innovative, dynamic, and
                future-ready IT solutions.{" "}
              </p>
            </div>
            <div className="mt-5 pt-4 border-t md:pb-0 pb-4 border-slate-100 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold tracking-wider text-slate-400 uppercase">
                  Focus Areas
                </p>
                <p className="text-sm font-medium text-slate-900 mt-0.5">
                  Enterprise BPM &bull; Intelligent Automation
                </p>
              </div>
              <a
                href="#services"
                className="group inline-flex items-center gap-1.5 text-sm font-semibold text-[#5fc9f0] hover:text-[#000036] transition-colors"
              >
                Our solutions
                <ArrowUpRight
                  size={16}
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </a>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="md:col-span-5 rounded-xl bg-[#000036] text-white md:p-8 p-6 sm:p-10 flex flex-col justify-between relative overflow-hidden shadow-md"
          >
            <div className="pointer-events-none absolute -top-12 -right-12 w-44 h-44 bg-violet-600/30 rounded-full blur-3xl" />
            <div className="pointer-events-none absolute top-0 right-0 w-3/5 opacity-40 z-0">
              <img src={bg} alt="" />
            </div>

            <div>
              <span className="text-xs font-semibold tracking-widest text-slate-400 uppercase">
                Collective Experience
              </span>
              <div className="mt-2 text-5xl sm:text-7xl font-light tracking-tighte text-white">
                <Counter value={100} suffix="+" />
              </div>
            </div>

            <div className="mt-8">
              <p className="text-base font-medium text-slate-200">
                Years of leadership experience in BPM &amp; LCNC ecosystems.
              </p>
              <p className="mt-1 text-base text-slate-400 leading-relaxed">
                Proven architectural leadership solving high-consequence
                enterprise challenges.
              </p>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="md:col-span-4 rounded-xl relative overflow-hidden border border-white md:p-8 p-6 flex flex-col justify-between shadow-sm">
            <div
              className="pointer-events-none absolute top-0 left-0 w-full h-full opacity-20 "
              style={{
                background:
                  "linear-gradient(135deg, #62d4f7 0%, #5dc9f0 40%, #3579be 60%",
              }}
            />
            <div>
              <span className="text-xs font-semibold tracking-widest text-[#000036] uppercase">
                Track Record
              </span>
              <div className="mt-2 counter-class  text-5xl sm:text-6xl font-light tracking-tighter text-[#000036]">
                <Counter value={10} suffix="+" />
              </div>
            </div>
            <p className="mt-6 text-base text-[#000036]">
              Years delivering digital process transformations across global
              enterprises.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="md:col-span-8 rounded-xl relative  backdrop-blur-md border border-white md:p-8 p-6 flex flex-col overflow-hidden justify-between shadow-sm"
          >
            <div
              className="pointer-events-none absolute top-0 left-0 w-full h-full opacity-20 "
              style={{
                background:
                  "linear-gradient(135deg, #62d4f7 0%, #5dc9f0 40%, #3579be 60%",
              }}
            />
            <div>
              <span className="text-xs font-semibold tracking-widest text-[#000036] uppercase">
                Capabilities &amp; Platforms
              </span>
              <p className="text-base text-[#000036] mt-1">
                Engineered for agility, scalability, and zero process friction.
              </p>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {PILLS.map((pill) => (
                <span
                  key={pill}
                  className="px-4 py-2 rounded-xl text-base sm:text-sm font-medium bg-[#f5f5f5] text-[#000036] border border-black/5 hover:border-black/20 hover:bg-white transition-all cursor-default"
                >
                  {pill}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
